import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { SEOMeta } from '@/templates/SEOMeta';
import { SeoInternalLinks } from '@/components/SeoInternalLinks';
import { generateFAQPageSchema, generateHowToSchema } from '@/utils/schema';
import { useTranslations } from 'next-intl';
import {
  CheckCircleIcon,
  RocketLaunchIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

function Badge({ children }) {
  return (
    <span className="inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700 shadow-sm">
      {children}
    </span>
  );
}

function Stat({ title, value, hint, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition-all hover:shadow-md">
      <div className="mb-2 flex items-center gap-3">
        <div className="rounded-lg bg-blue-50 p-2">
          <Icon className="h-5 w-5 text-blue-600" />
        </div>
        <div className="text-sm font-bold text-gray-500">{title}</div>
      </div>
      <div className="text-2xl font-black text-gray-900">{value}</div>
      {hint ? <div className="mt-1 text-xs font-medium text-gray-400">{hint}</div> : null}
    </div>
  );
}

function FeatureItem({ title, desc }) {
  return (
    <div className="flex gap-4 rounded-2xl p-4 transition-colors hover:bg-gray-50">
      <div className="mt-1">
        <CheckCircleIcon className="h-6 w-6 text-emerald-500" />
      </div>
      <div>
        <div className="font-bold text-gray-900">{title}</div>
        <div className="mt-1 text-sm leading-relaxed text-gray-600">{desc}</div>
      </div>
    </div>
  );
}

function Accordion({ title, children }) {
  return (
    <details className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-6 py-5 text-base font-bold text-gray-900 marker:content-none">
        <span>{title}</span>
        <span className="text-gray-400">+</span>
      </summary>
      <div className="px-6 pb-6 text-sm leading-relaxed text-gray-600">{children}</div>
    </details>
  );
}

export default function IntegrationLandingPage({
  seo,
  badge,
  title,
  subtitle,
  primaryCta,
  secondaryCta,
  heroPanel,
  stats,
  featuresTitle,
  featuresSubtitle,
  features,
  stepsTitle,
  stepsSubtitle,
  steps,
  experienceTitle,
  experienceDescription,
  experienceBullets,
  faqTitle,
  faqSubtitle,
  faqs,
  bottomCtaTitle,
  bottomCtaSubtitle,
  bottomPrimaryCta,
  bottomSecondaryCta
}) {
  const t = useTranslations('integrationLanding');
  const heroMediaRef = useRef(null);
  const [heroMediaScale, setHeroMediaScale] = useState(1);

  useEffect(() => {
    if (!heroPanel?.media || typeof window === 'undefined') return undefined;

    const getTargetScale = (viewportWidth) => {
      if (viewportWidth >= 1280) return 1.3;
      if (viewportWidth >= 1024) return 1.2;
      if (viewportWidth >= 640) return 1.05;
      return 1;
    };

    const measure = () => {
      const mediaEl = heroMediaRef.current;
      if (!mediaEl) return;

      const targetScale = getTargetScale(window.innerWidth);
      const rect = mediaEl.getBoundingClientRect();
      if (!rect.width) {
        setHeroMediaScale(targetScale);
        return;
      }

      const rootFontSize =
        Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
      const minRightGap = rootFontSize; // 1em
      const isDesktop = window.innerWidth >= 1024;
      const maxScaleForRightGap = isDesktop
        ? (window.innerWidth - minRightGap - rect.left) / rect.width
        : ((window.innerWidth - minRightGap - rect.left) * 2) / rect.width - 1;
      const nextScale = Math.max(1, Math.min(targetScale, maxScaleForRightGap));

      setHeroMediaScale((current) =>
        Math.abs(current - nextScale) < 0.001 ? current : nextScale
      );
    };

    let rafId = null;
    const scheduleMeasure = () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        measure();
      });
    };

    scheduleMeasure();
    window.addEventListener('resize', scheduleMeasure);

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined' && heroMediaRef.current) {
      resizeObserver = new ResizeObserver(scheduleMeasure);
      resizeObserver.observe(heroMediaRef.current);
    }

    return () => {
      window.removeEventListener('resize', scheduleMeasure);
      if (resizeObserver) resizeObserver.disconnect();
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, [heroPanel?.media]);

  const isShopifyIntegration = seo?.url === '/shopify-integration';
  const growthSection = isShopifyIntegration
    ? {
        title: t('growth.shopify.title'),
        description: t('growth.shopify.description'),
        bullets: [
          t('growth.shopify.bullets.0'),
          t('growth.shopify.bullets.1'),
          t('growth.shopify.bullets.2'),
          t('growth.shopify.bullets.3')
        ]
      }
    : {
        title: t('growth.default.title'),
        description: t('growth.default.description'),
        bullets: [
          t('growth.default.bullets.0'),
          t('growth.default.bullets.1'),
          t('growth.default.bullets.2'),
          t('growth.default.bullets.3')
        ]
      };

  return (
    <div className="bg-white font-sans antialiased text-slate-900 selection:bg-blue-100 selection:text-blue-900">
      <SEOMeta
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        url={seo.url}
        structuredData={[
          generateFAQPageSchema(faqs),
          generateHowToSchema({
            name: stepsTitle,
            description: stepsSubtitle,
            steps: steps.map((step) => ({ name: step.title, text: step.desc }))
          })
        ]}
      />

      <main>
        <section className="relative overflow-hidden bg-white pb-14 pt-14 md:pb-18 md:pt-18">
          <div className="mx-auto grid max-w-7xl items-center gap-10 px-6 lg:grid-cols-2">
            <div>
              <Badge>{badge}</Badge>
              <h1 className="mt-6 text-5xl font-black leading-[1.1] tracking-tight text-slate-900 md:text-6xl">
                {title}
              </h1>
              <p className="mt-8 max-w-xl text-xl leading-relaxed text-slate-600">{subtitle}</p>
              <div className="mt-10 flex flex-wrap gap-4">
                {primaryCta.external ? (
                  <a
                    href={primaryCta.href}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="rounded-2xl bg-blue-600 px-8 py-4 text-lg font-bold text-white shadow-xl shadow-blue-600/20 transition-all hover:-translate-y-1 hover:bg-blue-700"
                  >
                    {primaryCta.label}
                  </a>
                ) : (
                  <Link
                    href={primaryCta.href}
                    className="rounded-2xl bg-blue-600 px-8 py-4 text-lg font-bold text-white shadow-xl shadow-blue-600/20 transition-all hover:-translate-y-1 hover:bg-blue-700"
                  >
                    {primaryCta.label}
                  </Link>
                )}
                {secondaryCta.external ? (
                  <a
                    href={secondaryCta.href}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="rounded-2xl border border-slate-200 bg-white px-8 py-4 text-lg font-bold text-slate-700 transition-all hover:border-slate-300"
                  >
                    {secondaryCta.label}
                  </a>
                ) : (
                  <Link
                    href={secondaryCta.href}
                    className="rounded-2xl border border-slate-200 bg-white px-8 py-4 text-lg font-bold text-slate-700 transition-all hover:border-slate-300"
                  >
                    {secondaryCta.label}
                  </Link>
                )}
              </div>
            </div>

            <div className="relative">
              {heroPanel.media ? (
                <div className="relative w-full max-w-[700px] overflow-visible rounded-[2rem] lg:ml-auto lg:pl-[1em] lg:pr-[1em]">
                  <div
                    ref={heroMediaRef}
                    className="relative h-full w-full origin-center transition-transform lg:origin-left"
                    style={{ transform: `scale(${heroMediaScale})` }}
                  >
                    {heroPanel.media}
                    <div className="pointer-events-none absolute inset-y-0 left-0 w-[10%] bg-gradient-to-r from-white/95 to-transparent" />
                    <div className="pointer-events-none absolute inset-y-0 right-0 w-[10%] bg-gradient-to-l from-white/95 to-transparent" />
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-[10%] bg-gradient-to-b from-white/95 to-transparent" />
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[10%] bg-gradient-to-t from-white/95 to-transparent" />
                  </div>
                </div>
              ) : (
                <div className="relative overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white p-4 shadow-2xl">
                  <div className="flex aspect-[4/3] flex-col items-center justify-center rounded-[2rem] border border-slate-100 bg-slate-50 p-8 text-center">
                    <div className="mb-6 rounded-[2rem] bg-blue-600 p-5 shadow-xl shadow-blue-600/20">
                      {heroPanel.icon}
                    </div>
                    <h2 className="text-xl font-black tracking-tight text-slate-900">{heroPanel.title}</h2>
                    <p className="mt-2 max-w-[250px] text-sm leading-relaxed text-slate-500">{heroPanel.description}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="border-y border-slate-100 bg-white py-8">
          <div className="mx-auto max-w-7xl px-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {stats.map((item) => (
                <Stat key={item.title} {...item} />
              ))}
            </div>
          </div>
        </section>

        <section id="features" className="bg-gray-50/50 py-14">
          <div className="mx-auto max-w-7xl px-6">
            <div className="mb-12 text-center">
              <h2 className="text-4xl font-black tracking-tight text-slate-900">{featuresTitle}</h2>
              <p className="mt-4 text-lg text-slate-600">{featuresSubtitle}</p>
            </div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {features.map((item) => (
                <FeatureItem key={item.title} title={item.title} desc={item.desc} />
              ))}
            </div>
          </div>
        </section>

        <section className="overflow-hidden bg-white py-14">
          <div className="mx-auto max-w-7xl px-6">
            <div className="flex flex-col items-center gap-10 lg:flex-row">
              <div className="lg:w-1/2">
                <h2 className="text-4xl font-black leading-snug text-slate-900">{stepsTitle}</h2>
                <p className="mt-6 text-lg leading-relaxed text-slate-600">{stepsSubtitle}</p>
                <div className="mt-8 space-y-7">
                  {steps.map((step) => (
                    <div key={step.num} className="group flex gap-6">
                      <div className="text-4xl font-black leading-none text-blue-100 transition-colors group-hover:text-blue-600">
                        {step.num}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-slate-900">{step.title}</h3>
                        <p className="mt-2 leading-relaxed text-slate-600">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative lg:w-1/2">
                <div className="absolute -inset-10 rounded-full bg-blue-600/5 blur-[100px]" />
                <div className="relative overflow-hidden rounded-[40px] bg-slate-900 p-10 text-white shadow-2xl">
                  <div className="h-1.5 w-24 rounded-full bg-blue-500" />
                  <h3 className="mb-4 mt-8 text-2xl font-black text-blue-400">{experienceTitle}</h3>
                  <p className="mb-10 leading-relaxed text-slate-300">{experienceDescription}</p>
                  <div className="space-y-4">
                    {experienceBullets.map((item) => (
                      <div key={item} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-4">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500">
                          <CheckCircleIcon className="h-5 w-5" />
                        </div>
                        <span className="font-bold">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-slate-50 py-6 md:py-8">
          <div className="mx-auto grid max-w-7xl gap-8 px-6 lg:grid-cols-2 lg:items-center">
            <div className="flex items-center justify-center">
              <img
                src="/integration/sb.webp"
                alt="Customer advocacy growth with Afivio"
                className="h-auto w-[80%] scale-[1.1] object-contain"
              />
            </div>
            <div className="flex h-full flex-col justify-center lg:pl-4">
              <span className="inline-flex w-fit items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-blue-700">
                {t('customerLedGrowth')}
              </span>
              <h2 className="mt-4 text-4xl font-black leading-tight text-slate-900">{growthSection.title}</h2>
              <div className="mt-4 h-1 w-20 rounded-full bg-blue-500/70" />
              <p className="mt-6 text-lg leading-relaxed text-slate-600">{growthSection.description}</p>
              <ul className="mt-8 space-y-3">
                {growthSection.bullets.map((item) => (
                  <li key={item} className="flex gap-3 rounded-xl border border-slate-200 bg-white/80 px-4 py-3 text-slate-700 shadow-sm">
                    <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="bg-white py-14">
          <div className="mx-auto max-w-3xl px-6">
            <div className="mb-10 text-center">
              <h2 className="mb-4 text-4xl font-black text-slate-900">{faqTitle}</h2>
              <p className="text-lg text-slate-600">{faqSubtitle}</p>
            </div>
            <div className="space-y-3">
              {faqs.map((item) => (
                <Accordion key={item.q} title={item.q}>
                  {item.a}
                </Accordion>
              ))}
            </div>
          </div>
        </section>

        <section className="px-6 py-14">
          <div className="relative mx-auto max-w-5xl overflow-hidden rounded-[3rem] bg-blue-600 p-12 text-center md:p-16">
            <div className="pointer-events-none absolute left-0 top-0 h-full w-full bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent opacity-10" />
            <h2 className="relative z-10 text-4xl font-black leading-tight text-white md:text-5xl">
              {bottomCtaTitle}
            </h2>
            <p className="relative z-10 mb-10 mt-6 text-xl text-blue-100/80">{bottomCtaSubtitle}</p>
            <div className="relative z-10 flex flex-col justify-center gap-4 sm:flex-row">
              {bottomPrimaryCta.external ? (
                <a
                  href={bottomPrimaryCta.href}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="rounded-2xl bg-white px-10 py-5 text-xl font-bold text-blue-600 shadow-2xl transition-all hover:-translate-y-1"
                >
                  {bottomPrimaryCta.label}
                </a>
              ) : (
                <Link
                  href={bottomPrimaryCta.href}
                  className="rounded-2xl bg-white px-10 py-5 text-xl font-bold text-blue-600 shadow-2xl transition-all hover:-translate-y-1"
                >
                  {bottomPrimaryCta.label}
                </Link>
              )}
              {bottomSecondaryCta.external ? (
                <a
                  href={bottomSecondaryCta.href}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="rounded-2xl border border-blue-500/50 bg-blue-700 px-10 py-5 text-xl font-bold text-white transition-all hover:bg-blue-800"
                >
                  {bottomSecondaryCta.label}
                </a>
              ) : (
                <Link
                  href={bottomSecondaryCta.href}
                  className="rounded-2xl border border-blue-500/50 bg-blue-700 px-10 py-5 text-xl font-bold text-white transition-all hover:bg-blue-800"
                >
                  {bottomSecondaryCta.label}
                </Link>
              )}
            </div>

            <div className="mt-12 flex items-center justify-center gap-8 text-blue-200/60">
              <div className="flex items-center gap-2">
                <ShieldCheckIcon className="h-5 w-5" />
                <span className="text-xs font-bold uppercase tracking-widest">{t('secureSetup')}</span>
              </div>
              <div className="flex items-center gap-2">
                <RocketLaunchIcon className="h-5 w-5" />
                <span className="text-xs font-bold uppercase tracking-widest">{t('fastLaunch')}</span>
              </div>
            </div>
          </div>
        </section>

        <SeoInternalLinks />
      </main>
    </div>
  );
}
